import { ErrorHandler, Injectable, Injector, inject } from '@angular/core';
import * as Sentry from '@sentry/browser';
import { AppConfigService } from '@core/services/config/app-config.service';
import { ErrorRecoveryDialogComponent } from '@core/components/error-recovery-dialog/error-recovery-dialog.component';
import { SyrfDialogService } from '@shared/dialog/syrf-dialog.service';

@Injectable({ providedIn: 'root' })
export class SentryErrorHandler extends ErrorHandler {
  private _appConfig = inject(AppConfigService);
  private _isDialogOpen = false;
  /**
   * `SyrfDialogService` injects the NgRx `Store`, and this handler is created at bootstrap as the app's
   * `ErrorHandler`. Resolving the dialog service only when the recovery dialog opens keeps that dependency
   * (and any DI cycle through it) out of bootstrap.
   */
  private readonly _injector = inject(Injector);
  trackingOn = this._appConfig.config.errorTracking;

  constructor() {
    super();
    console.log(`Error Reporting is ${this.trackingOn ? 'On' : 'Off'}!`);
  }
  handleError(error) {
    if (
      this.trackingOn &&
      !error.message.includes('ExpressionChangedAfterItHasBeenCheckedError') &&
      !error.message.includes('WebSocket closed with status code: 1006')
    ) {
      // Always capture the error to Sentry regardless of dialog state
      const eventId = Sentry.captureException(error.originalError || error);

      // Only show dialog if one isn't already open
      if (!this._isDialogOpen) {
        this._isDialogOpen = true;

        this._showReportDialog({
          eventId,
          subtitle2:
            "If you'd like to help, tell us what happened below. The application may be in an unstable state and could behave unexpectedly until refreshed.",
          onClose: async () => {
            this._isDialogOpen = false; // Reset dialog state
            void ErrorRecoveryDialogComponent.open(
              this._injector.get(SyrfDialogService)
            );
          },
        });
      }
    }
    super.handleError(error);
  }

  /**
   * The Sentry report dialog, separate so specs can observe it: the unit-test builder bundles
   * `@sentry/browser` into a chunk shared with this handler in a full run, where
   * `vi.mock('@sentry/browser')` cannot reach this module's reference.
   */
  private _showReportDialog(options: Sentry.ReportDialogOptions): void {
    Sentry.showReportDialog(options);
  }
}
